import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Talalat { oktazon: string; nev: string; szul_datum: string | null; osztaly: string | null; aktiv: boolean; }
/** Iskolaváltással kilépett tanuló, akinek a kilépés körül nem készült csomag (GET /hianyzo). */
interface Hianyzo { oktazon: string; nev: string; osztaly: string | null; vege: string; kilepes_ok_kod: string | null; atvevo_intezmeny: string | null; }

/**
 *  Admin → Iskolaváltás: átadó csomag (backend/tanulo-atado.js).
 *
 *  Nkt. 41. § (8a): iskolaváltáskor az iskola átadja az új iskolának a tanuló tantárgyankénti
 *  előrehaladását igazoló adatait, tanév közbeni váltásnál az évközi jegyeit is. Itt készül el a
 *  csomag (JSON a másik rendszernek, PDF emberi olvasásra), és itt vehető át egy másik SULINFORM
 *  csomagja. Az átvett csomag NEM kerül a jegyek és a mulasztások közé – „átvett előzmény”-ként
 *  olvasható. Semmi nem megy ki magától: a fájlt az admin adja át a saját csatornáján.
 */
@Component({
  selector: 'app-admin-tanulo-atado',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './tanulo-atado.component.html',
  styleUrls: ['./tanulo-atado.component.scss'],
})
export class AdminTanuloAtadoComponent implements OnInit {
  intezmeny = '';
  kereses = '';
  talalatok: Talalat[] = [];
  tanulo: Talalat | null = null;
  tanev = '';
  fut = false;
  hiba = '';

  //  Fogadás
  feltoltott: any = null;
  feltoltottNev = '';
  atvetelMegj = '';
  atvetelHiba = '';

  //  Átvett előzmények
  elozmenyek: any[] = [];
  nyitott: any = null;
  nyitottId: string | null = null;

  //  Iskolaváltással kilépett, de csomag nélküli tanulók (2026-11-02)
  hianyzok: Hianyzo[] = [];

  constructor(private http: HttpClient, private data: DataService, private route: ActivatedRoute) {}

  async ngOnInit() {
    this.intezmeny = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.elozmenyBetolt();
    await this.hianyzokBetolt();
    //  A Kiléptetés képernyő „Átadó csomag készítése” gombja ?oktazon=…-nal nyit ide.
    const okt = String(this.route.snapshot.queryParamMap.get('oktazon') || '').trim();
    if (okt) await this.valasztOktazon(okt);
  }

  /** Iskolaváltással kilépett tanulók, akiknek nem készült átadó csomag (az elmúlt egy évből). */
  async hianyzokBetolt() {
    if (!this.intezmeny) return;
    try { this.hianyzok = await firstValueFrom(this.http.get<Hianyzo[]>(this.ut('hianyzo'))) || []; }
    catch { this.hianyzok = []; }
  }

  /** Kiválasztás oktatási azonosítóval (a keresőn át, pontos egyezésre). */
  async valasztOktazon(oktazon: string) {
    try {
      const l = await firstValueFrom(this.http.get<Talalat[]>(`${this.ut('tanulok')}?q=${encodeURIComponent(oktazon)}`));
      const t = (l || []).find((x) => x.oktazon === oktazon);
      if (t) await this.valaszt(t);
    } catch (e: any) { this.hiba = e?.error?.error || 'A tanuló nem tölthető be.'; }
  }

  private ut(vege: string) { return `/api/tanulo-atado/${vege}/${this.intezmeny}`; }

  async keres() {
    const k = this.kereses.trim();
    if (k.length < 2) { this.talalatok = []; return; }
    try { this.talalatok = await firstValueFrom(this.http.get<Talalat[]>(`${this.ut('tanulok')}?q=${encodeURIComponent(k)}`)); }
    catch (e: any) { this.hiba = e?.error?.error || 'A keresés nem sikerült.'; }
  }

  async valaszt(t: Talalat) {
    this.tanulo = t; this.talalatok = []; this.kereses = '';
    this.feltoltott = null; this.feltoltottNev = ''; this.atvetelHiba = ''; this.nyitott = null; this.nyitottId = null;
    await this.elozmenyBetolt();
  }

  async tanuloTorol() { this.tanulo = null; this.feltoltott = null; this.nyitott = null; this.nyitottId = null; await this.elozmenyBetolt(); }

  private tanevParam() { return /^\d{4}$/.test(this.tanev.trim()) ? `&tanev=${this.tanev.trim()}` : ''; }

  async jsonLetolt() {
    if (!this.tanulo) return;
    this.fut = true; this.hiba = '';
    try {
      const c: any = await firstValueFrom(this.http.get(`${this.ut('csomag')}?oktazon=${encodeURIComponent(this.tanulo.oktazon)}${this.tanevParam()}`));
      const blob = new Blob([JSON.stringify(c, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `atado-${this.tanulo.oktazon}-${c?.tanev?.ev || ''}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      await this.hianyzokBetolt();
    } catch (e: any) { this.hiba = e?.error?.error || 'A csomag nem készült el.'; }
    finally { this.fut = false; }
  }

  async pdf() {
    if (!this.tanulo) return;
    await this.data.megnyitFajl(`${this.ut('pdf')}?oktazon=${encodeURIComponent(this.tanulo.oktazon)}${this.tanevParam()}`,
      `Átadó csomag – ${this.tanulo.nev}`);
    await this.hianyzokBetolt();
  }

  fajlValasztas(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const f = input.files?.[0];
    this.feltoltott = null; this.atvetelHiba = ''; this.feltoltottNev = f?.name || '';
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { this.atvetelHiba = 'A fájl túl nagy (legfeljebb 5 MB).'; return; }
    const r = new FileReader();
    r.onload = () => {
      try {
        const c = JSON.parse(String(r.result || ''));
        if (c?.formatum !== 'sulinform-tanulo-atado') { this.atvetelHiba = 'Ez nem Sulinform átadó csomag.'; return; }
        this.feltoltott = c;
        if (this.tanulo && c?.tanulo?.oktatasi_azonosito !== this.tanulo.oktazon) {
          this.atvetelHiba = `A csomag egy másik tanulóé (${c?.tanulo?.oktatasi_azonosito || '?'}), nem a kiválasztotté.`;
        }
      } catch { this.atvetelHiba = 'A fájl nem olvasható JSON.'; }
    };
    r.readAsText(f, 'utf-8');
    input.value = '';
  }

  nev(n: any): string { return [n?.elotag, n?.vezeteknev, n?.utonev].filter(Boolean).join(' '); }

  async atvesz() {
    if (!this.tanulo || !this.feltoltott) return;
    this.fut = true; this.atvetelHiba = '';
    try {
      await firstValueFrom(this.http.post(this.ut('atvetel'), { oktazon: this.tanulo.oktazon, csomag: this.feltoltott, megjegyzes: this.atvetelMegj }));
      this.data.sendToast('A csomag átvett előzményként rögzítve.', 'success');
      this.feltoltott = null; this.feltoltottNev = ''; this.atvetelMegj = '';
      await this.elozmenyBetolt();
    } catch (e: any) { this.atvetelHiba = e?.error?.error || 'Az átvétel nem sikerült.'; }
    finally { this.fut = false; }
  }

  async elozmenyBetolt() {
    if (!this.intezmeny) return;
    try {
      const p = this.tanulo ? `?oktazon=${encodeURIComponent(this.tanulo.oktazon)}` : '';
      this.elozmenyek = await firstValueFrom(this.http.get<any[]>(`${this.ut('atvetel')}${p}`));
    } catch (e: any) { this.hiba = e?.error?.error || 'Az átvett előzmények nem tölthetők be.'; }
  }

  async megnyit(e: any) {
    if (this.nyitottId === e.id) { this.nyitott = null; this.nyitottId = null; return; }
    try {
      this.nyitott = await firstValueFrom(this.http.get(`/api/tanulo-atado/atvetel-adat/${e.id}/${this.intezmeny}`));
      this.nyitottId = e.id;
    } catch (x: any) { this.data.sendToast(x?.error?.error || 'Nem nyitható meg.', 'error'); }
  }

  async elozmenyPdf(e: any) {
    await this.data.megnyitFajl(`/api/tanulo-atado/atvetel-pdf/${e.id}/${this.intezmeny}`, `Átvett előzmény – ${e.nev || e.oktazon}`);
  }

  async visszavon(e: any) {
    const ok = prompt('Miért vonod vissza ezt az átvett csomagot? (pl. téves tanulóhoz töltötték fel)');
    if (!ok || !ok.trim()) return;
    try {
      await firstValueFrom(this.http.post(`/api/tanulo-atado/atvetel-visszavon/${e.id}/${this.intezmeny}`, { ok: ok.trim() }));
      this.data.sendToast('Visszavonva.', 'success');
      await this.elozmenyBetolt();
    } catch (x: any) { this.data.sendToast(x?.error?.error || 'A visszavonás nem sikerült.', 'error'); }
  }

  /** Az átvett csomag évközi jegyei tantárgyanként (a részletes nézethez). */
  jegyekTantargyankent(c: any): { tantargy: string; jegyek: any[] }[] {
    const m = new Map<string, any[]>();
    for (const j of c?.evkozi_jegyek || []) {
      const k = j.tantargy || '–';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(j);
    }
    return [...m.entries()].map(([tantargy, jegyek]) => ({ tantargy, jegyek }));
  }
}
